export interface CategoryConfig {
  slug: string;
  displayName: string;
  icon: string;
  description: string;
}

/**
 * Master list of all 14 news categories.
 * Used for sidebar navigation, routing, and filtering.
 */
export const categories: CategoryConfig[] = [
  {
    slug: 'dashboard',
    displayName: 'Dashboard',
    icon: 'LayoutDashboard',
    description: 'Overview of today\'s mining intelligence across all categories.',
  },
  // Market Insights sits directly after the Daily Brief (which the sidebar injects
  // right below Dashboard) — prices, trends and market analysis are the second
  // thing an analyst reaches for after the morning brief.
  {
    slug: 'market-insights',
    displayName: 'Market Insights',
    icon: 'TrendingUp',
    description: 'Market analysis, trends, and investment intelligence for the mining sector.',
  },
  {
    slug: 'mining-companies',
    displayName: 'Mining Companies News',
    icon: 'Factory',
    description: 'News and updates about mining companies operating across Africa.',
  },
  {
    slug: 'commodities',
    displayName: 'Resources & Energy',
    icon: 'Gem',
    description: 'Minerals, oil & gas, power & renewables — prices, supply-demand dynamics and sector news across Africa, with an AI synthesis.',
  },
  // Infrastructures and Artisanal Mining sit directly under Resources & Energy
  // per editorial priority.
  {
    slug: 'infrastructure',
    displayName: 'Infrastructures',
    icon: 'TrainFront',
    description: 'Mining infrastructure, logistics, ports, rail, and energy developments.',
  },
  {
    slug: 'artisanal-mining',
    displayName: 'Artisanal Mining',
    icon: 'Pickaxe',
    description: 'Artisanal and small-scale mining (ASM) developments and formalization efforts.',
  },
  // The money pair sits directly under Artisanal Mining. Named so the split is
  // legible from the sidebar alone: the two used to be "Financial News" and
  // "Financing", which shared the financ- root and blurred together. Results &
  // Performance covers what a company EARNED and PRODUCED; Capital & Deals
  // covers how money is RAISED and DEPLOYED. Slugs are unchanged, so the
  // classifier, the routes and the digest service are untouched by the rename.
  {
    slug: 'financial-results',
    displayName: 'Results & Performance',
    icon: 'DollarSign',
    description: 'Company performance: earnings, revenue, margins, dividends, guidance and production results.',
  },
  {
    slug: 'financing',
    displayName: 'Capital & Deals',
    icon: 'Banknote',
    description: 'Capital and deals: debt, equity raises, IPOs, project finance, offtake and streaming, M&A.',
  },
  // The state pair sits directly under Capital & Deals: what the state rules,
  // then what the state reports.
  // Merged Aug 2026: the old Regulatory and Legal & Taxes tabs are one tab on
  // the surviving 'regulatory' slug, so existing links and bookmarks still work.
  // /legal-taxes-royalties redirects here (see App.tsx).
  {
    slug: 'regulatory',
    displayName: 'Mining Governance',
    icon: 'Scale',
    description: 'Mining codes, permits and titles, tax and royalties, courts and arbitration, enforcement, and the geopolitics of mineral access.',
  },
  {
    slug: 'government-figures',
    displayName: 'Government Data',
    icon: 'Building2',
    description: 'Official government statistics, production data, and policy announcements.',
  },
  // Africa Focus retired Aug 2026. In a portal that IS African mining
  // intelligence the category was tautological: its rule was "African country +
  // mining context", which caught 22% of everything, 82% of it already sitting
  // in another tab and 87% already reachable from a region page. The name now
  // belongs to the Regions section in the sidebar, where it is actually true.
  {
    slug: 'consultancy-reports',
    displayName: 'Reports & Studies',
    icon: 'FileText',
    description: 'Industry reports, consultancy studies, and research publications.',
  },
  {
    slug: 'internal-reports',
    displayName: 'Internal Reports',
    icon: 'Upload',
    description: 'Proprietary StratMined reports and internal analysis documents.',
  },
];

/**
 * Get a category by its slug.
 */
export function getCategoryBySlug(slug: string): CategoryConfig | undefined {
  return categories.find((c) => c.slug === slug);
}

/**
 * Get all navigable categories (excluding dashboard which is a special route).
 */
export function getNavigableCategories(): CategoryConfig[] {
  return categories.filter((c) => c.slug !== 'dashboard');
}
